import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import {
  BatchDetailPresenter,
  type BatchOutcome,
} from '@/components/campaign-import/batch-detail.presenter';
import {
  campaignImportBatchesControllerCancel,
  campaignImportBatchesControllerConfirm,
  campaignImportBatchesControllerGet,
} from '@/generated/korza-api-client';
import type { CampaignImportBatchActionDto } from '@/generated/korza-api-client/types.gen';
import { CAMPAIGNS_PAGE_SIZE, useCampaigns } from '@/hooks/use-campaigns';
import { useTenant } from '@/hooks/use-tenant';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { useI18nContext } from '@/i18n/i18n-react';

type BatchAction = {
  kind: 'confirm' | 'cancel';
  campaignIds: CampaignImportBatchActionDto['campaignIds'];
};

export default function CampaignImportBatchPage() {
  const tenant = useTenant();
  const { LL } = useI18nContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const batchId = useParams().batchId ?? '';
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<BatchOutcome | null>(null);

  const batchQuery = useQuery({
    queryKey: ['campaign-import-batch', tenant.slug, batchId],
    queryFn: async () => {
      const response = await campaignImportBatchesControllerGet({
        path: { tenantSlug: tenant.slug, batchId },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: batchId !== '',
  });
  const campaignsQuery = useCampaigns(
    { importBatchId: batchId },
    CAMPAIGNS_PAGE_SIZE,
    (page - 1) * CAMPAIGNS_PAGE_SIZE
  );

  const actionMutation = useMutation({
    mutationFn: async ({ kind, campaignIds }: BatchAction) => {
      // `campaignIds` undefined = whole batch. The presenter never passes an empty list.
      const options = {
        path: { tenantSlug: tenant.slug, batchId },
        body: { campaignIds },
        throwOnError: true,
      } as const;
      if (kind === 'confirm') {
        const response = await campaignImportBatchesControllerConfirm(options);
        return { kind, result: response.data } as const;
      }
      const response = await campaignImportBatchesControllerCancel(options);
      return { kind, result: response.data } as const;
    },
    onSuccess: async (done) => {
      setOutcome(done);
      setSelectedIds([]);
      setPage(1);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns', tenant.slug] }),
        queryClient.invalidateQueries({
          queryKey: ['campaign-import-batch', tenant.slug, batchId],
        }),
      ]);
    },
    onError: () => setOutcome({ kind: 'error' }),
  });

  const campaignsPath = `/${tenant.slug}/campaigns`;

  if (batchQuery.isError || campaignsQuery.isError) {
    return (
      <SidebarLayout>
        <p role="alert" className="py-10 text-center text-muted-foreground">
          {batchQuery.isError
            ? LL.campaignImport.batch.loadFailed()
            : LL.campaignImport.batch.campaignsLoadFailed()}
        </p>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      {batchQuery.data && campaignsQuery.data ? (
        <BatchDetailPresenter
          batch={batchQuery.data}
          campaigns={campaignsQuery.data.data}
          total={campaignsQuery.data.total}
          page={page}
          pageSize={CAMPAIGNS_PAGE_SIZE}
          onPageChange={(next) => {
            setPage(next);
            setSelectedIds([]);
          }}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          isActing={actionMutation.isPending}
          outcome={outcome}
          onDismissOutcome={() => setOutcome(null)}
          onConfirm={(campaignIds) =>
            actionMutation.mutate({ kind: 'confirm', campaignIds })
          }
          onCancel={(campaignIds) =>
            actionMutation.mutate({ kind: 'cancel', campaignIds })
          }
          onOpenCampaign={(id) => navigate(`${campaignsPath}/${id}`)}
          onBack={() => navigate(campaignsPath)}
          onImportAnother={() => navigate(`${campaignsPath}/import`)}
        />
      ) : null}
    </SidebarLayout>
  );
}
